import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  FormHelperText,
  Switch,
  Tooltip,
  MenuItem,
  FormControl,
  InputLabel,
  TextField as TextFieldMUI,
  Typography,
  CircularProgress,
} from "@mui/material";
import { TextField, Select, fieldToTextField } from "formik-mui";
import { useDispatch, useSelector } from "react-redux";
import Autocomplete from "@mui/lab/Autocomplete";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";

import { addSource, editSource } from "../../../../store/ducks/duckSource";
import { getUsers } from "../../../../store/ducks/duckUsers";
import useDebounce from "../../../../hooks/useDebounce";
import { useUser } from "../../../../auth/UserProvider";
import { AVAILABLE_CONFIDENTIALITY, AVAILABLE_PLATFORMS } from "../../../../constants/general";
import { BorderLinearProgress } from "../../../../components/LoadingComponent";

// eslint-disable-next-line react/prop-types
const FormikAutocomplete = ({ textFieldProps, ...props }) => {
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const dispatch = useDispatch();
  const debouncedSearchTerm = useDebounce(searchInput, 500);

  const {
    // eslint-disable-next-line react/prop-types
    form: { setTouched, setFieldValue },
  } = props;
  const { error, helperText, ...field } = fieldToTextField(props);
  const { name } = field;

  useEffect(
    () => {
      if (debouncedSearchTerm) {
        setLoading(true);
        // Fire off our API call
        dispatch(getUsers(searchInput)).then((_) => setLoading(false));
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchTerm],
  );

  return (
    <Autocomplete
      {...props}
      {...field}
      getOptionLabel={({ firstName, lastName, networkUser }) => {
        return `${networkUser} ${firstName} ${lastName}`;
      }}
      loading={loading}
      renderInput={(props) => (
        <TextFieldMUI
          {...props}
          {...textFieldProps}
          InputProps={{
            // eslint-disable-next-line react/prop-types
            ...props.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}

                {/* eslint-disable-next-line react/prop-types */}
                {props.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          error={error}
          helperText={helperText}
        />
      )}
      renderOption={({ firstName, lastName, networkUser }, _state) => (
        <Typography>
          {networkUser} <Typography color="textSecondary">{`${firstName} ${lastName}`}</Typography>
        </Typography>
      )}
      onBlur={() => setTouched({ [name]: true })}
      onChange={(_, value) => setFieldValue(name, value)}
      onInputChange={(event, newInputValue) => {
        event.preventDefault();
        setSearchInput(newInputValue);
      }}
    />
  );
};

const SourceForm = (props) => {
  const { initialValues, isEdit, handleClose, open } = props;
  const { isAdmin } = useUser();
  const users = useSelector((state) => state.users.data);

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  return (
    <>
      <Formik
        initialValues={initialValues}
        validate={(values) => {
          let errors = {};

          if (!values.name || values.name === "") {
            errors.name = "Required";
          }

          return errors;
        }}
        validateOnBlur={false}
        onSubmit={(values, { setSubmitting }) => {
          return values.id > 0
            ? dispatch(editSource(values, values.id)).then(() => {
                handleClose();
                setSubmitting(false);
                enqueueSnackbar(`Edited '${values.name}' Source Successfully`, {
                  variant: "success",
                });
              })
            : dispatch(addSource(values)).then(() => {
                handleClose();
                setSubmitting(false);
                enqueueSnackbar(`Added '${values.name}' Source Successfully`, {
                  variant: "success",
                });
              });
        }}
      >
        {({ submitForm, isSubmitting, values, setFieldValue }) => (
          <Form>
            <Dialog
              fullWidth
              aria-labelledby="form-dialog-title"
              maxWidth="sm"
              open={open}
              onClose={handleClose}
            >
              {isSubmitting && <BorderLinearProgress />}
              <DialogTitle id="form-dialog-title">
                {isEdit && initialValues.id !== 0
                  ? "Edit"
                  : !isEdit && initialValues.id !== 0
                    ? "Details"
                    : "Add"}

                {isAdmin && values.id !== 0 && (
                  <>
                    <Tooltip
                      key={`Helper-deleted-admin-tooltip`}
                      title={values.deleted ? "Enable" : "Disable"}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.deleted}
                            name="deleted"
                            onChange={() => setFieldValue("deleted", !values.deleted)}
                          />
                        }
                        label="Deleted"
                        style={{ marginRight: 0, marginLeft: "80%" }}
                      />
                    </Tooltip>
                    {/* TODO:  Ver si es Necesario agregar información para aclarar permisos de admin */}
                    {/* 
                        <Grid container>
                          <Tooltip key={`Helper-admin-tooltip-info`} title={'You can execute this action due to your administrator permissions'}  >
                            <InfoIcon color={'disabled'} />
                          </Tooltip>
                        </Grid> 
                      */}
                  </>
                )}
              </DialogTitle>
              <DialogContent style={{ overflow: "hidden" }}>
                <Grid container spacing={4} styles={{ margin: "auto" }} xs={12}>
                  <Grid item xs={5}>
                    <Field
                      fullWidth
                      component={TextField}
                      disabled={values.deleted}
                      label="Name"
                      margin="dense"
                      name="name"
                      type="text"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Grid
                      container
                      alignItems="center"
                      direction="row"
                      justify="center"
                      spacing={1}
                      style={{ minHeight: "100%" }}
                    >
                      <Grid item xs={3}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.redirect}
                              color="primary"
                              disabled={values.deleted}
                              name="redirect"
                              onChange={() => setFieldValue("redirect", !values.redirect)}
                            />
                          }
                          label="Redirect"
                        />
                      </Grid>
                      <Grid item xs={9}>
                        {values.redirect && (
                          <FormHelperText>
                            If the source redirects, the url opens in another window
                          </FormHelperText>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={5}>
                    <Grid
                      container
                      alignItems="center"
                      direction="row"
                      justify="center"
                      spacing={1}
                      style={{ minHeight: "100%" }}
                    >
                      <FormControl fullWidth margin="dense" variant="outlined">
                        <InputLabel id={`Label-Confidentiality`}>Confidentiality</InputLabel>
                        <Field
                          component={Select}
                          disabled={values.deleted}
                          inputProps={{
                            variant: "outlined",
                            fullWidth: true,
                          }}
                          label="Confiditiality"
                          labelId={"Label-Confiditiality"}
                          margin="dense"
                          name="confidentiality"
                          style={{ width: "100%", height: "100%" }}
                          type="number"
                          variant="outlined"
                        >
                          {AVAILABLE_CONFIDENTIALITY.map((mI, i) => (
                            <MenuItem key={`MenuItem-option-${mI}-${i}`} value={i}>
                              {mI}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <FormControl fullWidth margin="dense" variant="outlined">
                      <InputLabel id={`Label-Platform`}>Platform</InputLabel>
                      <Field
                        component={Select}
                        disabled={values.deleted}
                        inputProps={{
                          variant: "outlined",
                          fullWidth: true,
                        }}
                        label="Platform"
                        labelId={"Label-Platform"}
                        margin="dense"
                        name="platform"
                        style={{ width: "100%", height: "100%" }}
                        type="number"
                        variant="outlined"
                      >
                        {AVAILABLE_PLATFORMS.map((mI, i) => (
                          <MenuItem key={`MenuItem-option-${mI}-${i}`} value={i}>
                            {mI}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <Field
                      fullWidth
                      component={TextField}
                      disabled={values.deleted}
                      label="Plaform Id"
                      margin="dense"
                      name="platformId"
                      style={{ height: "100%" }}
                      type="text"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Field
                      multiple
                      component={FormikAutocomplete}
                      disabled={values.deleted}
                      margin="dense"
                      name="owners"
                      options={users}
                      style={{ width: "100%", height: "100%" }}
                      textFieldProps={{
                        fullWidth: true,
                        margin: "dense",
                        variant: "outlined",
                        label: "Owners",
                      }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={4} styles={{ margin: "auto" }}>
                  <Grid item xs={12}>
                    <Field
                      fullWidth
                      multiline
                      component={TextField}
                      disabled={values.deleted}
                      label="Url"
                      margin="dense"
                      name="url"
                      rows={4}
                      type="text"
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button color="primary" variant="contained" onClick={handleClose}>
                  Close
                </Button>
                {isEdit && (
                  <Button color="primary" variant="contained" onClick={submitForm}>
                    Save
                  </Button>
                )}
              </DialogActions>
            </Dialog>
          </Form>
        )}
      </Formik>
    </>
  );
};

SourceForm.propTypes = {
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool,
  handleClose: PropTypes.func,
  open: PropTypes.func,
};

export default SourceForm;
